import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { Plus, Edit, Trash2, Video, Key, AlertCircle, Loader, Save, X, Users } from 'lucide-react';

export default function AdminPage() {
  const {
    isAdmin,
    courses,
    videos,
    serialNumbers,
    isLoading,
    error,
    checkAdminStatus,
    createCourse,
    updateCourse,
    deleteCourse,
    addVideo,
    generateSerialNumbers,
    fetchAdminCourses,
    fetchCourseVideos,
    clearError
  } = useAdminStore();

  const [activeTab, setActiveTab] = useState<'courses' | 'videos' | 'serials'>('courses');
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showSerialForm, setShowSerialForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any>(null);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    duration_months: 1,
    price: 0
  });

  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    video_url: '',
    duration_seconds: 0,
    order_index: 1
  });

  const [serialForm, setSerialForm] = useState({
    course_id: 0,
    count: 1
  });

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminCourses();
    }
  }, [isAdmin, fetchAdminCourses]);

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await createCourse(courseForm);
    if (success) {
      setShowCourseForm(false);
      setCourseForm({ title: '', description: '', thumbnail_url: '', duration_months: 1, price: 0 });
    }
  };

  const handleEditCourse = (course: any) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description || '',
      thumbnail_url: course.thumbnail_url || '',
      duration_months: course.duration_months,
      price: course.price
    });
    setShowCourseForm(true);
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCourse) {
      const success = await updateCourse(editingCourse.id, courseForm);
      if (success) {
        setShowCourseForm(false);
        setEditingCourse(null);
        setCourseForm({ title: '', description: '', thumbnail_url: '', duration_months: 1, price: 0 });
      }
    }
  };

  const handleDeleteCourse = async (courseId: number) => {
    if (confirm('هل أنت متأكد من حذف هذا الكورس؟')) {
      await deleteCourse(courseId);
    }
  };

  const handleAddVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCourse) {
      const success = await addVideo(selectedCourse, videoForm);
      if (success) {
        setShowVideoForm(false);
        setVideoForm({ title: '', description: '', video_url: '', duration_seconds: 0, order_index: 1 });
        fetchCourseVideos(selectedCourse);
      }
    }
  };

  const handleGenerateSerials = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await generateSerialNumbers(serialForm.course_id, serialForm.count);
    if (success) {
      setShowSerialForm(false);
    }
  };

  const selectCourse = (courseId: number) => {
    setSelectedCourse(courseId);
    fetchCourseVideos(courseId);
    setActiveTab('videos');
  };

  if (isLoading && !isAdmin) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-stone-600">جاري التحقق من الصلاحيات...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">غير مصرح لك</h2>
          <p className="text-stone-600">ليس لديك صلاحيات للوصول إلى لوحة الإدارة</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">
            لوحة الإدارة
          </h1>
          <p className="text-stone-600">
            إدارة الكورسات والمحتوى والاشتراكات
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mr-auto text-red-600 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-stone-200">
            <nav className="flex space-x-8 rtl:space-x-reverse px-6">
              <button
                onClick={() => setActiveTab('courses')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'courses'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                إدارة الكورسات
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'videos'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
                disabled={!selectedCourse}
              >
                إدارة الفيديوهات
              </button>
              <button
                onClick={() => setActiveTab('serials')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'serials'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                إنشاء السيريال نمبر
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-stone-900">قائمة الكورسات</h2>
                  <button
                    onClick={() => setShowCourseForm(true)}
                    className="bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center space-x-2 rtl:space-x-reverse"
                  >
                    <Plus className="w-4 h-4" />
                    <span>إضافة كورس جديد</span>
                  </button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.map((course) => (
                    <div key={course.id} className="bg-stone-50 rounded-lg p-6 border border-stone-200">
                      <h3 className="text-lg font-bold text-stone-900 mb-2">{course.title}</h3>
                      <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                        {course.description || 'لا يوجد وصف'}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-stone-500 mb-4">
                        <span>{course.duration_months} شهر</span>
                        <span>{course.price} ر.س</span>
                        <span>{course.video_count || 0} فيديو</span>
                      </div>

                      <div className="flex space-x-2 rtl:space-x-reverse">
                        <button
                          onClick={() => selectCourse(course.id)}
                          className="flex-1 bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                        >
                          <Video className="w-4 h-4" />
                          <span>الفيديوهات</span>
                        </button>
                        <button
                          onClick={() => handleEditCourse(course)}
                          className="flex-1 bg-green-600 text-white py-2 px-3 rounded text-sm hover:bg-green-700 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                        >
                          <Edit className="w-4 h-4" />
                          <span>تعديل</span>
                        </button>
                        <button
                          onClick={() => handleDeleteCourse(course.id)}
                          className="flex-1 bg-red-600 text-white py-2 px-3 rounded text-sm hover:bg-red-700 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>حذف</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos Tab */}
            {activeTab === 'videos' && selectedCourse && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-stone-900">
                    مقاطع الفيديو - {courses.find(c => c.id === selectedCourse)?.title}
                  </h2>
                  <button
                    onClick={() => setShowVideoForm(true)}
                    className="bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center space-x-2 rtl:space-x-reverse"
                  >
                    <Plus className="w-4 h-4" />
                    <span>إضافة فيديو</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {videos.map((video) => (
                    <div key={video.id} className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-stone-900">{video.title}</h3>
                          <p className="text-stone-600 text-sm mt-1">{video.description}</p>
                          <div className="flex items-center space-x-4 rtl:space-x-reverse text-xs text-stone-500 mt-2">
                            <span>الترتيب: {video.order_index}</span>
                            {video.duration_seconds && (
                              <span>المدة: {Math.floor(video.duration_seconds / 60)}:{(video.duration_seconds % 60).toString().padStart(2, '0')}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2 rtl:space-x-reverse">
                          <button className="text-green-600 hover:text-green-700">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Serials Tab */}
            {activeTab === 'serials' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-stone-900">إنشاء السيريال نمبر</h2>
                  <button
                    onClick={() => setShowSerialForm(true)}
                    className="bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center space-x-2 rtl:space-x-reverse"
                  >
                    <Key className="w-4 h-4" />
                    <span>إنشاء سيريال جديد</span>
                  </button>
                </div>

                {serialNumbers.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-green-800 mb-3">السيريال نمبر الجديدة:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {serialNumbers.map((serial, index) => (
                        <div key={index} className="bg-white p-2 rounded border font-mono text-sm">
                          {serial}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Course Form Modal */}
        {showCourseForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-stone-900">
                  {editingCourse ? 'تعديل الكورس' : 'إضافة كورس جديد'}
                </h3>
                <button
                  onClick={() => {
                    setShowCourseForm(false);
                    setEditingCourse(null);
                    setCourseForm({ title: '', description: '', thumbnail_url: '', duration_months: 1, price: 0 });
                  }}
                  className="text-stone-500 hover:text-stone-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">عنوان الكورس</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">وصف الكورس</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">رابط صورة الكورس</label>
                  <input
                    type="url"
                    value={courseForm.thumbnail_url}
                    onChange={(e) => setCourseForm({...courseForm, thumbnail_url: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">مدة الاشتراك (أشهر)</label>
                    <input
                      type="number"
                      min="1"
                      value={courseForm.duration_months}
                      onChange={(e) => setCourseForm({...courseForm, duration_months: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">السعر (ر.س)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({...courseForm, price: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 rtl:space-x-reverse pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-amber-600 text-white py-2 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                  >
                    <Save className="w-4 h-4" />
                    <span>{editingCourse ? 'تحديث' : 'إنشاء'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCourseForm(false);
                      setEditingCourse(null);
                    }}
                    className="flex-1 bg-stone-200 text-stone-700 py-2 rounded-lg font-semibold hover:bg-stone-300 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Video Form Modal */}
        {showVideoForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-stone-900">إضافة فيديو جديد</h3>
                <button
                  onClick={() => setShowVideoForm(false)}
                  className="text-stone-500 hover:text-stone-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleAddVideo} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">عنوان الفيديو</label>
                  <input
                    type="text"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">وصف الفيديو</label>
                  <textarea
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">رابط الفيديو</label>
                  <input
                    type="url"
                    value={videoForm.video_url}
                    onChange={(e) => setVideoForm({...videoForm, video_url: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">المدة (ثانية)</label>
                    <input
                      type="number"
                      min="0"
                      value={videoForm.duration_seconds}
                      onChange={(e) => setVideoForm({...videoForm, duration_seconds: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">الترتيب</label>
                    <input
                      type="number"
                      min="1"
                      value={videoForm.order_index}
                      onChange={(e) => setVideoForm({...videoForm, order_index: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 rtl:space-x-reverse pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-amber-600 text-white py-2 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                  >
                    <Save className="w-4 h-4" />
                    <span>إضافة الفيديو</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVideoForm(false)}
                    className="flex-1 bg-stone-200 text-stone-700 py-2 rounded-lg font-semibold hover:bg-stone-300 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Serial Form Modal */}
        {showSerialForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-stone-900">إنشاء سيريال نمبر</h3>
                <button
                  onClick={() => setShowSerialForm(false)}
                  className="text-stone-500 hover:text-stone-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleGenerateSerials} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">اختر الكورس</label>
                  <select
                    value={serialForm.course_id}
                    onChange={(e) => setSerialForm({...serialForm, course_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  >
                    <option value={0}>اختر كورس...</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">عدد السيريال نمبر</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={serialForm.count}
                    onChange={(e) => setSerialForm({...serialForm, count: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="flex space-x-3 rtl:space-x-reverse pt-4">
                  <button
                    type="submit"
                    disabled={isLoading || serialForm.course_id === 0}
                    className="flex-1 bg-amber-600 text-white py-2 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-1 rtl:space-x-reverse"
                  >
                    <Key className="w-4 h-4" />
                    <span>إنشاء السيريال</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSerialForm(false)}
                    className="flex-1 bg-stone-200 text-stone-700 py-2 rounded-lg font-semibold hover:bg-stone-300 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}